import { useState, useEffect, useCallback } from "react";
import { UserPlus, UserCheck, Search, Phone, Mail, MessageCircle, Plus } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import AdminReferralModal from "../components/referrals/AdminReferralModal";
import api from "../services/api";

var STATUS_CLS = { nova: "bg-amber-100 text-amber-700", atribuida: "bg-blue-100 text-blue-700", em_contato: "bg-purple-100 text-purple-700", convertida: "bg-emerald-100 text-emerald-700", perdida: "bg-red-100 text-red-700" };
var STATUS_LABELS = { nova: "Nova", atribuida: "Atribuida", em_contato: "Em Contato", convertida: "Convertida", perdida: "Perdida" };

function cleanPhone(phone) {
  return (phone || "").replace(/\D/g, "");
}

function ReferralCard({ referral, brokers, onAssign, onStatus }) {
  var sCls = STATUS_CLS[referral.status] || "bg-gray-100 text-gray-500";
  var sLbl = STATUS_LABELS[referral.status] || referral.status;
  var digits = cleanPhone(referral.phone);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid={"referral-card-" + referral.id}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-[#0A2F3D]">{referral.name}</p>
          <p className="text-xs text-gray-500">Indicado por: <span className="font-medium">{referral.referred_by_name}</span></p>
        </div>
        <span className={"px-2 py-0.5 rounded-full text-xs font-medium " + sCls}>{sLbl}</span>
      </div>
      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <Phone className="w-3 h-3" />{referral.phone}
          {digits && (
            <a href={"https://wa.me/55" + digits} target="_blank" rel="noopener noreferrer"
              className="ml-1 text-emerald-500 hover:text-emerald-600" title="WhatsApp" data-testid={"referral-wpp-" + referral.id}>
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        {referral.email && <p className="text-xs text-gray-600 flex items-center gap-1"><Mail className="w-3 h-3" />{referral.email}</p>}
        {referral.product_interest && <p className="text-xs text-gray-500">Interesse: <span className="font-medium capitalize">{referral.product_interest.replace("_", " ")}</span></p>}
        {referral.notes && <p className="text-xs text-gray-400 italic">{referral.notes}</p>}
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        {referral.assigned_broker_name ? (
          <div className="flex items-center gap-1 text-xs text-blue-600"><UserCheck className="w-3 h-3" />{referral.assigned_broker_name}</div>
        ) : (
          <select onChange={function(e) { if (e.target.value) onAssign(referral.id, e.target.value); }} className="px-2 py-1 border rounded-lg text-xs" defaultValue="" data-testid={"assign-broker-" + referral.id}>
            <option value="">Atribuir a...</option>
            {brokers.map(function(b) { return <option key={b.id} value={b.id}>{b.name}</option>; })}
          </select>
        )}
        {referral.status !== "convertida" && referral.status !== "perdida" && (
          <select onChange={function(e) { if (e.target.value) onStatus(referral.id, e.target.value); }} className="px-2 py-1 border rounded-lg text-xs" defaultValue="" data-testid={"status-referral-" + referral.id}>
            <option value="">Mover para...</option>
            <option value="em_contato">Em Contato</option>
            <option value="convertida">Convertida</option>
            <option value="perdida">Perdida</option>
          </select>
        )}
      </div>
    </div>
  );
}

export default function ReferralsPage() {
  var referralsState = useState([]);
  var referrals = referralsState[0];
  var setReferrals = referralsState[1];
  var brokersState = useState([]);
  var brokers = brokersState[0];
  var setBrokers = brokersState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var filterState = useState("");
  var filter = filterState[0];
  var setFilter = filterState[1];
  var showCreateState = useState(false);

  var fetchData = useCallback(function() {
    setLoading(true);
    var qs = filter ? "?status=" + filter : "";
    Promise.all([
      api.get("/referrals" + qs),
      api.get("/users?role=broker"),
    ]).then(function(results) {
      setReferrals(results[0].data.referrals || []);
      var usersList = results[1].data.users || [];
      setBrokers(usersList);
    }).finally(function() { setLoading(false); });
  }, [filter]);

  useEffect(function() { fetchData(); }, [fetchData]);

  function handleAssign(id, brokerId) {
    api.put("/referrals/" + id + "/assign", { broker_id: brokerId }).then(function() { fetchData(); });
  }

  function handleStatus(id, status) {
    api.put("/referrals/" + id + "/status", { status: status }).then(function() { fetchData(); });
  }

  return (
    <AppShell activePage="indicacoes" title="Indicacoes">
      <div data-testid="referrals-page">
        <AdminReferralModal open={showCreateState[0]}
          onClose={function() { showCreateState[1](false); }}
          onCreated={function() { showCreateState[1](false); fetchData(); }} />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-lg font-bold text-[#0A2F3D]">Indicacoes de Clientes</h2>
            <p className="text-xs text-gray-500">{referrals.length} indicacao(oes)</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              {["", "nova", "atribuida", "em_contato", "convertida", "perdida"].map(function(s) {
                var active = filter === s;
                var lbl = s ? (STATUS_LABELS[s] || s) : "Todas";
                return (
                  <button key={s} onClick={function() { setFilter(s); }}
                    className={"px-3 py-1.5 rounded-lg text-xs font-medium transition-all " + (active ? "bg-[#3B82F6] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}
                    data-testid={"filter-" + (s || "all")}
                  >{lbl}</button>
                );
              })}
            </div>
            <button onClick={function() { showCreateState[1](true); }}
              className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-xl text-xs font-medium hover:bg-purple-700 transition-colors"
              data-testid="new-referral-btn">
              <Plus className="w-3.5 h-3.5" />Nova Indicacao
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Nenhuma indicacao encontrada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {referrals.map(function(r) { return <ReferralCard key={r.id} referral={r} brokers={brokers} onAssign={handleAssign} onStatus={handleStatus} />; })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
